import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMenu } from '../../../contexts/MenuContext';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const { isMobileMenuOpen, setIsMobileMenuOpen } = useMenu();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const hasUserScrolled = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  const currentPath = location.pathname;
  const isHomePage = currentPath === '/';

  // Detectar sección visible en la home page
  useEffect(() => {
    if (!isHomePage) {
      setActiveSection(null);
      return;
    }

    const sectionIds = ['problem-section', 'solution-section', 'final-cta'];
    const observers: IntersectionObserver[] = [];

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      // Solo actualizar si el usuario ha hecho scroll manualmente
      if (!hasUserScrolled.current) return;
      
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    // Pequeño delay para asegurar que los elementos del DOM están montados
    const timeoutId = setTimeout(() => {
      sectionIds.forEach(id => {
        const element = document.getElementById(id);
        if (element) {
          const observer = new IntersectionObserver(handleIntersect, {
            rootMargin: '-30% 0px -60% 0px',
            threshold: 0,
          });
          observer.observe(element);
          observers.push(observer);
        }
      });
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      observers.forEach(obs => obs.disconnect());
    };
  }, [isHomePage, currentPath]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Marcar que el usuario ha hecho scroll
      if (window.scrollY > 100 && !hasUserScrolled.current) {
        hasUserScrolled.current = true;
      }
      
      // Si estamos en home y arriba del todo, marcar Inicio como activo
      if (isHomePage && window.scrollY < 200) {
        setActiveSection(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  // Navegación a secciones del home (funciona desde cualquier página)
  const navigateToHomeSection = useCallback((sectionId: string) => {
    setIsMobileMenuOpen(false);
    if (isHomePage) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { scrollTo: sectionId } });
    }
  }, [isHomePage, navigate]);

  const goToHome = useCallback(() => {
    setIsMobileMenuOpen(false);
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  }, [isHomePage, navigate]);

  const goToPage = useCallback((path: string) => {
    setIsMobileMenuOpen(false);
    navigate(path);
  }, [navigate]);

  // Determinar si un item del navbar está activo
  const isActive = (type: 'home' | 'section' | 'page', identifier: string): boolean => {
    if (type === 'home') {
      return isHomePage && !activeSection;
    }
    if (type === 'section') {
      return isHomePage && activeSection === identifier;
    }
    if (type === 'page') {
      return currentPath === identifier;
    }
    return false;
  };

  const getDesktopClasses = (type: 'home' | 'section' | 'page', identifier: string): string => {
    const active = isActive(type, identifier);
    return `font-medium transition-colors cursor-pointer whitespace-nowrap relative ${
      active
        ? 'text-[#6D28D9] after:absolute after:bottom-[-6px] after:left-0 after:right-0 after:h-[2px] after:bg-[#6D28D9] after:rounded-full'
        : 'text-gray-700 hover:text-[#6D28D9]'
    }`;
  };

  const getMobileClasses = (type: 'home' | 'section' | 'page', identifier: string): string => {
    const active = isActive(type, identifier);
    return `flex items-center w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer text-[13px] font-medium ${
      active
        ? 'text-[#6D28D9] bg-[#6D28D9]/8 font-semibold'
        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
    }`;
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-soft' 
          : 'bg-white'
      }`}
    >
      {/* Desktop bar */}
      <div className="hidden lg:block w-full px-6 py-4">
        <div className="flex items-center justify-center relative">
          {/* Logo */}
          <div className="absolute left-0 flex items-center gap-3">
            <img 
              src="/images/logo-prebed.png" 
              alt="PreBedAI" 
              className="h-14 w-auto object-contain cursor-pointer"
              onClick={goToHome}
            />
          </div>

          {/* Desktop Navigation - Centered */}
          <div className="flex items-center gap-8">
            <button onClick={goToHome} className={getDesktopClasses('home', '')}>Inicio</button>
            <button onClick={() => navigateToHomeSection('problem-section')} className={getDesktopClasses('section', 'problem-section')}>Problema</button>
            <button onClick={() => navigateToHomeSection('solution-section')} className={getDesktopClasses('section', 'solution-section')}>Solución</button>
            <button onClick={() => goToPage('/modulos')} className={getDesktopClasses('page', '/modulos')}>Módulos</button>
            <button onClick={() => goToPage('/integraciones')} className={getDesktopClasses('page', '/integraciones')}>Integración</button>
            <button onClick={() => goToPage('/faq')} className={getDesktopClasses('page', '/faq')}>FAQ</button>
            <button onClick={() => navigateToHomeSection('final-cta')} className={getDesktopClasses('section', 'final-cta')}>Contacto</button>
            <button onClick={() => goToPage('/nosotros')} className={getDesktopClasses('page', '/nosotros')}>Nosotros</button>
          </div>

          {/* Login Button - Right */}
          <div className="absolute right-0">
            <button 
              onClick={() => window.location.href = 'https://prebedai.mct-origin.es'}
              className="btn-primary whitespace-nowrap"
            >
              Iniciar sesión
            </button>
          </div>
        </div>
      </div>

      {/* Mobile bar */}
      <div className="lg:hidden w-full px-4 py-2.5">
        <div className="flex items-center justify-between">
          {/* Logo - más pequeño en móvil */}
          <img 
            src="/images/logo-prebed.png" 
            alt="PreBedAI" 
            className="h-9 w-auto object-contain cursor-pointer"
            onClick={goToHome}
          />

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-50 border border-gray-200 cursor-pointer text-gray-700"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu - Fullscreen overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="lg:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>

          {/* Slide-in panel */}
          <div className="lg:hidden fixed top-0 right-0 bottom-0 w-[280px] bg-white z-50 shadow-2xl flex flex-col animate-slide-in-right">
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <span className="text-sm font-bold text-gray-900 uppercase tracking-wider">Menú</span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50 cursor-pointer text-gray-500 hover:text-gray-700"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Nav items */}
            <div className="flex-1 overflow-y-auto py-3 px-4 space-y-0.5">
              <button onClick={goToHome} className={getMobileClasses('home', '')}>
                <i className="ri-home-4-line text-base mr-3 opacity-60"></i>Inicio
              </button>
              <button onClick={() => navigateToHomeSection('problem-section')} className={getMobileClasses('section', 'problem-section')}>
                <i className="ri-error-warning-line text-base mr-3 opacity-60"></i>Problema
              </button>
              <button onClick={() => navigateToHomeSection('solution-section')} className={getMobileClasses('section', 'solution-section')}>
                <i className="ri-lightbulb-line text-base mr-3 opacity-60"></i>Solución
              </button>

              <div className="h-px bg-gray-100 my-2"></div>

              <button onClick={() => goToPage('/modulos')} className={getMobileClasses('page', '/modulos')}>
                <i className="ri-grid-line text-base mr-3 opacity-60"></i>Módulos
              </button>
              <button onClick={() => goToPage('/integraciones')} className={getMobileClasses('page', '/integraciones')}>
                <i className="ri-links-line text-base mr-3 opacity-60"></i>Integración
              </button>
              <button onClick={() => goToPage('/faq')} className={getMobileClasses('page', '/faq')}>
                <i className="ri-question-line text-base mr-3 opacity-60"></i>FAQ
              </button>

              <div className="h-px bg-gray-100 my-2"></div>

              <button onClick={() => navigateToHomeSection('final-cta')} className={getMobileClasses('section', 'final-cta')}>
                <i className="ri-mail-line text-base mr-3 opacity-60"></i>Contacto
              </button>
              <button onClick={() => goToPage('/nosotros')} className={getMobileClasses('page', '/nosotros')}>
                <i className="ri-team-line text-base mr-3 opacity-60"></i>Nosotros
              </button>
            </div>

            {/* Bottom CTA */}
            <div className="px-4 py-4 border-t border-gray-100">
              <button 
                onClick={() => window.location.href = 'https://prebedai.mct-origin.es'}
                className="block w-full btn-primary text-center text-sm py-2.5"
              >
                <i className="ri-login-box-line mr-2"></i>Iniciar sesión
              </button>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}
